import React, { useState } from "react";
import {
    Button,
    Flex,
    Input,
    Text,
    FormControl,
    FormErrorMessage,
} from "@chakra-ui/react";
import ModalLayout from "components/ModalLayout/ModalLayout";
import { Modals } from "constants/modals";
import { useAppContext } from "hooks/app";
import { useCaptureAbandonedCheckout } from "hooks/abandonedCheckout";
import getLifetimeCheckoutLink from "services/Subscriprion/getLifetimeCheckoutLink";
import getCheckoutLink from "services/Subscriprion/getCheckoutLink";
import { PlanType } from "enum/planTypes";

const LeadCaptureModal: React.FC = () => {
    const { closeModal, appState } = useAppContext();
    const { capture } = useCaptureAbandonedCheckout();
    const planType: string | undefined = appState?.modalData?.planType;
    const planName: string | undefined = appState?.modalData?.planName;
    const isLifetime = !planType || planType === PlanType.LIFETIME;
    // Caller may pass an explicit source; otherwise derive from plan type
    const source: string = appState?.modalData?.source ?? (isLifetime ? "lifetime" : "pricing");

    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateEmail = (value: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleSubmit = async () => {
        if (!validateEmail(email)) {
            setEmailError("Please enter a valid email address");
            return;
        }
        setEmailError("");
        setIsSubmitting(true);

        // Fire-and-forget lead capture, then redirect to Stripe checkout
        capture({ email, phone: phone || undefined, source, plan: planName ?? "Lifetime Plan" });

        try {
            const url = isLifetime
                ? await getLifetimeCheckoutLink()
                : await getCheckoutLink(planType!, planName);
            closeModal?.();
            window.location.href = url;
        } catch {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalLayout modal={Modals.LEAD_CAPTURE}>
            <Flex
                direction="column"
                gap={5}
                p="40px"
                w={{ base: "100%", md: "480px" }}
            >
                {/* Header */}
                <Flex direction="column" gap={1}>
                    <Text
                        fontSize="22px"
                        fontWeight="700"
                        color="#0A1931"
                        lineHeight="1.3"
                    >
                        Get an Exclusive Discount
                    </Text>
                    <Text fontSize="14px" color="#555" lineHeight="1.6">
                        Enter your details below and we'll send you a special offer for the
                        Lifetime Plan.
                    </Text>
                </Flex>

                {/* Email */}
                <FormControl isInvalid={!!emailError}>
                    <Text fontSize="13px" fontWeight="500" color="#0A1931" mb={1}>
                        Email Address <span style={{ color: "#E53E3E" }}>*</span>
                    </Text>
                    <Input
                        placeholder="you@example.com"
                        type="email"
                        value={email}
                        onChange={e => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError("");
                        }}
                        size="lg"
                        borderColor="#E2E8F0"
                        _focus={{ borderColor: "#185ADB", boxShadow: "0 0 0 1px #185ADB" }}
                    />
                    <FormErrorMessage>{emailError}</FormErrorMessage>
                </FormControl>

                {/* WhatsApp / Phone */}
                <FormControl>
                    <Text fontSize="13px" fontWeight="500" color="#0A1931" mb={1}>
                        WhatsApp Number{" "}
                        <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
                    </Text>
                    <Input
                        placeholder="+92 300 1234567"
                        type="tel"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        size="lg"
                        borderColor="#E2E8F0"
                        _focus={{ borderColor: "#185ADB", boxShadow: "0 0 0 1px #185ADB" }}
                    />
                    <Text fontSize="12px" color="#888" mt={1}>
                        For personalised support from our team
                    </Text>
                </FormControl>

                {/* Submit */}
                <Button
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    size="lg"
                    bg="#185ADB"
                    color="white"
                    borderRadius="6px"
                    _hover={{ bg: "#1249b8" }}
                    _active={{ bg: "#0e3a92" }}
                    fontSize="15px"
                    fontWeight="600"
                    mt={1}
                >
                    Send Me the Discount
                </Button>

                <Text fontSize="12px" color="#888" textAlign="center">
                    We respect your privacy. No spam, ever.
                </Text>
            </Flex>
        </ModalLayout>
    );
};

export default LeadCaptureModal;
